import React, { useState } from 'react';
import { ChevronRight, Search, RefreshCw } from 'lucide-react';
import { Input } from '@client/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import { USAGE_METRICS_CONFIG } from '@shared/config/usageMetricsConfig';
import { AdvisorUsageTableSkeleton } from './AdvisorUsageTableSkeleton';
import { formatMetricValue, buildMetricTooltip } from '@/utils/metricFormatting';

interface AdvisorUsageTableProps {
  advisors: AdvisorUsageData[];
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export function AdvisorUsageTable({ advisors, onRefresh, isRefreshing }: AdvisorUsageTableProps) {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const toggleRow = (advisorId: string) => {
    setExpandedRowId(expandedRowId === advisorId ? null : advisorId);
  };

  const filteredAdvisors = advisors.filter(advisor => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      advisor.advisorName.toLowerCase().includes(search) ||
      advisor.advisorEmail.toLowerCase().includes(search) ||
      advisor.organizationName.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative w-72">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        <Input
          type="text"
          placeholder="Search advisors..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          className="pl-10 pr-9"
        />
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Advisor Name</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Account Age</TableHead>
              <TableHead>Total Logins</TableHead>
              <TableHead className="text-right">Fixed Premiums</TableHead>
              <TableHead className="text-right">Clients Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isRefreshing ? (
              <AdvisorUsageTableSkeleton />
            ) : filteredAdvisors.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center"
                >
                  <p className="text-gray-700">
                    {searchText
                      ? 'No advisors found matching your search.'
                      : 'No advisor data available.'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredAdvisors.map((advisor, index) => {
                const isExpanded = expandedRowId === advisor.advisorId;
                const isLastRow = index === filteredAdvisors.length - 1;

                return (
                  <React.Fragment key={advisor.advisorId}>
                    {/* Main Row */}
                    <TableRow
                      className="cursor-pointer transition-colors hover:bg-gray-100"
                      onClick={() => toggleRow(advisor.advisorId)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="hover:bg-primary-100 flex h-8 w-8 items-center justify-center rounded-full transition-colors">
                            <ChevronRight
                              className={`h-5 w-5 text-gray-700 transition-transform ${
                                isExpanded ? 'rotate-90' : ''
                              }`}
                            />
                          </div>
                          <span className="font-medium text-gray-900">{advisor.advisorName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-gray-900">
                        {advisor.organizationName}
                      </TableCell>
                      <TableCell className="text-gray-700">{advisor.advisorEmail}</TableCell>
                      <TableCell className="text-gray-900">{advisor.accountAge}</TableCell>
                      <TableCell className="text-gray-900">
                        {advisor.usageMetrics.totalAdvisorLogins}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">
                        ${(advisor.usageMetrics.totalFixedPremiums ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-900">
                        {advisor.usageMetrics.totalInvitedClients +
                          advisor.usageMetrics.totalClientsWithSurveysCompleted}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Metrics Row */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="bg-gray-50 px-6 py-6"
                        >
                          <div className="ml-8">
                            <h4 className="mb-4 text-sm font-bold text-gray-900">Usage Metrics</h4>
                            <TooltipProvider>
                              <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-6">
                                {USAGE_METRICS_CONFIG.map(metricDef => (
                                  <MetricItem
                                    key={metricDef.key}
                                    label={metricDef.label}
                                    value={advisor.usageMetrics[metricDef.key]}
                                    description={metricDef.description}
                                    format={metricDef.format}
                                  />
                                ))}
                              </div>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function MetricItem({
  label,
  value,
  description,
  format,
}: {
  label: string;
  value: string | number;
  description?: string;
  format?: 'number' | 'currency' | 'percentage';
}) {
  const { formattedValue, fullValue } = React.useMemo(
    () => formatMetricValue(value, format),
    [value, format],
  );

  const tooltipText = buildMetricTooltip(fullValue, description);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help rounded-lg border border-gray-200 bg-white p-3">
          <p className="mb-1 text-xs text-gray-700">{label}</p>
          <p className="text-lg font-bold text-gray-900">{formattedValue}</p>
        </div>
      </TooltipTrigger>
      {tooltipText && (
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
